import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { CalendarDays, Plus, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export default function HolidaysPage() {
  const { user } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [form, setForm] = useState({ date: '', name: '', description: '' });
  const isSuperadmin = user?.role === 'superadmin';

  const fetchHolidays = () =>
    api.get('/api/admin/holidays').then((res) => setHolidays(res.data.holidays || []));

  useEffect(() => {
    fetchHolidays().finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!form.date || !form.name.trim()) {
      toast.error('Date and name are required');
      return;
    }
    try {
      await api.post('/api/admin/holidays', form);
      toast.success('Holiday added');
      setCreateOpen(false);
      setForm({ date: '', name: '', description: '' });
      await fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this holiday? Cancelled sessions will not be restored.')) return;
    try {
      await api.delete(`/api/admin/holidays/${id}`);
      toast.success('Holiday removed');
      await fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const confirmCancelClasses = async () => {
    if (!cancelTarget) return;
    setCancelBusy(true);
    try {
      const res = await api.post(`/api/admin/holidays/${cancelTarget.id}/cancel-classes`);
      toast.success(
        `Cancelled ${res.data.sessions_cancelled} session(s). ` +
          `${res.data.learners_notified} learners + ${res.data.instructors_notified} instructors notified.`
      );
      setCancelTarget(null);
      await fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel classes');
    } finally {
      setCancelBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="holidays-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <p className="text-sm text-muted-foreground">
            Mark academy-wide holidays. Use "Cancel classes" to stop all sessions on that date and
            auto-email affected learners &amp; instructors.
          </p>
        </div>
        {isSuperadmin && (
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="add-holiday-btn"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Holiday
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            All Holidays ({holidays.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {holidays.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground text-sm">
              No holidays added yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Holiday</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays.map((h) => (
                  <TableRow key={h.id} data-testid={`holiday-row-${h.id}`}>
                    <TableCell className="font-mono text-sm">{formatDate(h.date)}</TableCell>
                    <TableCell className="font-medium">{h.name}</TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-xs truncate">
                      {h.description || '—'}
                    </TableCell>
                    <TableCell>
                      {h.classes_cancelled ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Classes cancelled ({h.affected_sessions})
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not triggered</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      {isSuperadmin && !h.classes_cancelled && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setCancelTarget(h)}
                          data-testid={`cancel-classes-${h.id}`}
                        >
                          Cancel classes
                        </Button>
                      )}
                      {isSuperadmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(h.id)}
                          data-testid={`delete-holiday-${h.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Public Holiday</DialogTitle>
            <DialogDescription>
              You can trigger class cancellation separately after creating the holiday.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Date</label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="holiday-date-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Holiday name</label>
              <Input
                placeholder="e.g. Independence Day"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="holiday-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description (optional)</label>
              <Textarea
                rows={2}
                placeholder="Shared with learners in the email"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="holiday-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} data-testid="save-holiday-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel classes confirmation */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Cancel all classes on {cancelTarget?.date}?
            </DialogTitle>
            <DialogDescription>
              Every upcoming session on this date will be cancelled. All booked learners and
              affected instructors will receive an email. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>
              Keep classes
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancelClasses}
              disabled={cancelBusy}
              data-testid="confirm-cancel-classes-btn"
            >
              {cancelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
